<template>
  <div>
    <v-dialog :value="value" persistent :max-width="isEditMode ? 560 : 960" @input="$emit('input', $event)">
      <v-card>
        <v-card-title>
          {{
            isEditMode
              ? $t('crudLinhas.detail.allocation.title')
              : $t('crudLinhas.detail.allocation.bulkTitle')
          }}
        </v-card-title>

      <!-- Edição de passageiro já alocado -->
      <template v-if="isEditMode">
        <v-card-subtitle v-if="passageiro">
          {{ passageiro.nome_completo }}
          <v-chip
            v-if="isValidado(passageiro)"
            x-small
            color="success"
            class="ml-2"
            text-color="white"
          >
            {{ $t('crudLinhas.detail.allocation.validated') }}
          </v-chip>
          <v-chip v-else x-small color="grey" class="ml-2" text-color="white">
            {{ passageiro.nome_situacao_validacao || $t('crudLinhas.detail.allocation.notValidated') }}
          </v-chip>
        </v-card-subtitle>
        <v-card-text>
          <v-text-field
            :value="linhaLabel"
            :label="$t('crudLinhas.detail.allocation.line')"
            dense
            outlined
            readonly
            hide-details
            class="mb-3"
          />
          <div class="d-flex flex-wrap mb-3">
            <v-switch
              v-model="form.alocar_ida"
              :label="$t('crudLinhas.detail.passengers.outbound')"
              dense
              hide-details
              class="mr-6 mt-0"
              :true-value="true"
              :false-value="false"
            />
            <v-switch
              v-model="form.alocar_volta"
              :label="$t('crudLinhas.detail.passengers.inbound')"
              dense
              hide-details
              class="mt-0"
              :true-value="true"
              :false-value="false"
            />
          </div>
          <LazySelect
            ref="grupoSelect"
            class="mb-3"
            get-url="getGruposlazy"
            text="nome"
            value="id_grupo"
            :label="$t('crudLinhas.detail.passengers.group')"
            @input="onGrupoSelect"
          />
          <v-autocomplete
            v-model="form.id_cerca_embarque"
            :items="cercasAlocaveis"
            item-text="nome_cerca"
            item-value="id_cerca"
            :label="$t('crudLinhas.detail.passengers.boarding')"
            dense
            outlined
            clearable
            hide-details
            class="mb-3"
            @change="onFormBoardingChange"
          />
          <v-autocomplete
            v-model="form.id_cerca_desembarque"
            :items="cercasAlocaveis"
            item-text="nome_cerca"
            item-value="id_cerca"
            :label="$t('crudLinhas.detail.passengers.alighting')"
            dense
            outlined
            clearable
            hide-details
            :disabled="!lineHasRoundTrip()"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="close">{{ $t('button.cancel') }}</v-btn>
          <v-btn color="primary" :loading="saving" :disabled="!canSaveEdit" @click="saveEdit">
            {{ $t('button.save') }}
          </v-btn>
        </v-card-actions>
      </template>

        <!-- Alocação em lote com verificação -->
        <template v-else>
          <v-card-subtitle v-if="linha">
            {{ linha.cod_linha }} — {{ linha.nome_linha }}
          </v-card-subtitle>
          <v-card-text>
            <MultiplaEscolha
              ref="passageirosSelect"
              class="mb-3"
              :items="candidatos"
              text="nome_completo"
              value="id_pessoa"
              :label="$t('crudLinhas.detail.allocation.selectPassengers')"
              can-toggle-all
              :disabled="verifying || saving || loadingCandidatos"
              @update:selectedItems="selectedIds = $event"
            />

            <div class="d-flex flex-wrap align-center mb-3">
              <v-btn
                color="primary"
                class="mr-2 mb-2"
                :loading="verifying"
                :disabled="!selectedIds.length || saving"
                @click="verificar"
              >
                <v-icon left small>mdi-magnify</v-icon>
                {{ $t('crudLinhas.detail.allocation.verify') }}
              </v-btn>
              <span v-if="verifying" class="caption grey--text">
                {{ $t('crudLinhas.detail.allocation.verifying') }}
              </span>
            </div>

            <v-alert v-if="verified && !validatedRows.length" type="info" dense outlined class="mb-3">
              {{ $t('crudLinhas.detail.allocation.noResults') }}
            </v-alert>

            <v-data-table
              v-if="validatedRows.length"
              :headers="resultHeaders"
              :items="validatedRows"
              dense
              :items-per-page="10"
              item-key="id_pessoa"
              class="elevation-1"
            >
              <template v-slot:item.elegivel="{ item }">
                <v-chip x-small :color="rowStatusColor(item)" text-color="white">
                  {{
                    item.elegivel && !item.motivo
                      ? $t('crudLinhas.detail.allocation.eligible')
                      : item.motivo
                  }}
                </v-chip>
                <v-tooltip top v-if="item.tempoLinhaExcede">
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on" x-small color="orange darken-3" class="ml-1">mdi-clock-alert-outline</v-icon>
                  </template>
                  <div>{{ item.tempoLinhaTooltip }}</div>
                </v-tooltip>
              </template>
              <template v-slot:item.id_cerca_embarque="{ item }">
                <v-autocomplete
                  v-model="item.id_cerca_embarque"
                  :items="cercasAlocaveis"
                  item-text="nome_cerca"
                  item-value="id_cerca"
                  dense
                  hide-details
                  clearable
                  :disabled="!item.elegivel || verifying || saving"
                  :label="$t('crudLinhas.detail.passengers.boarding')"
                  @change="onRowBoardingChange(item)"
                />
              </template>
              <template v-slot:item.id_cerca_desembarque="{ item }">
                <v-autocomplete
                  v-model="item.id_cerca_desembarque"
                  :items="cercasAlocaveis"
                  item-text="nome_cerca"
                  item-value="id_cerca"
                  dense
                  hide-details
                  clearable
                  :disabled="!item.elegivel || verifying || saving || !lineHasRoundTrip()"
                  :label="$t('crudLinhas.detail.passengers.alighting')"
                />
              </template>
              <template v-slot:item.id_grupo="{ item }">
                <v-autocomplete
                  v-model="item.id_grupo"
                  :items="grupos"
                  item-text="nome"
                  item-value="id_grupo"
                  dense
                  hide-details
                  clearable
                  :disabled="!item.elegivel || verifying || saving"
                  :label="$t('crudLinhas.detail.passengers.group')"
                />
              </template>
            </v-data-table>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text :disabled="verifying || saving" @click="close">
              {{ $t('button.cancel') }}
            </v-btn>
            <v-btn
              color="primary"
              :loading="saving"
              :disabled="!canAllocate || verifying"
              @click="alocar"
            >
              {{ $t('crudLinhas.detail.allocation.allocate') }}
            </v-btn>
          </v-card-actions>
        </template>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import apiController from "@/utils/apiController";
import EventBus from "@/utils/eventBus";
import MultiplaEscolha from "@/components/Selects/MultiplaEscolha.vue";
import LazySelect from "@/components/Selects/LazySelect.vue";
import { haversineMeters } from "@/utils/haversine";
import { VALIDO_CRIACAO_PONTO } from "@/utils/allocationFenceCreation";
import { SITUACAO_VALIDACAO, isValidado } from "@/utils/situacoesValidacao";

export default {
  name: "AlocaPassageiroLinhaDialog",
  components: { MultiplaEscolha, LazySelect },
  props: {
    value: { type: Boolean, default: false },
    /** Quando informado, abre modo edição de um passageiro já alocado. */
    passageiro: { type: Object, default: null },
    linha: { type: Object, default: null },
    historicoSessaoId: { type: String, default: null },
    historicoSessaoOrigemId: { type: String, default: null },
    cercasLinha: { type: Array, default: () => [] },
    grupos: { type: Array, default: () => [] },
    candidatos: { type: Array, default: () => [] },
    loadingCandidatos: { type: Boolean, default: false },
  },
  data() {
    return {
      saving: false,
      verifying: false,
      verified: false,
      selectedIds: [],
      validatedRows: [],
      form: {
        id_grupo: null,
        id_cerca_embarque: null,
        id_cerca_desembarque: null,
        alocar_ida: true,
        alocar_volta: true,
      },
      schedulerPoll: { timer: null, inFlight: false },
    };
  },
  computed: {
    isEditMode() {
      return this.passageiro != null && this.passageiro.id_pessoa != null;
    },
    linhaLabel() {
      if (!this.linha) return "";
      return `${this.linha.cod_linha || ""} — ${this.linha.nome_linha || ""}`;
    },
    cercasAlocaveis() {
      const seen = new Map();
      const out = [];
      for (const c of this.cercasLinha || []) {
        if (!c || c.id_cerca == null) continue;
        const tipo = Number(c.id_tipo_cerca);
        if (tipo === 1 || tipo === 4) continue;
        const id = Number(c.id_cerca);
        if (!Number.isFinite(id) || id <= 0) continue;
        const rowSentido = c.id_sentido != null ? Number(c.id_sentido) : null;
        if (seen.has(id)) {
          const existing = seen.get(id);
          const prev = Number(existing.id_sentido);
          if (
            Number.isFinite(prev) &&
            Number.isFinite(rowSentido) &&
            prev !== rowSentido
          ) {
            existing.id_sentido = -1;
          }
          continue;
        }
        const item = { ...c, id_cerca: id, id_sentido: Number.isFinite(rowSentido) ? rowSentido : null };
        seen.set(id, item);
        out.push(item);
      }
      return out;
    },
    canSaveEdit() {
      return (
        this.linha &&
        this.linha.id_linha != null &&
        this.passageiro &&
        this.passageiro.id_pessoa != null &&
        (this.form.alocar_ida || this.form.alocar_volta)
      );
    },
    resultHeaders() {
      return [
        { text: this.$t("crudLinhas.detail.passengers.name"), value: "nome_completo" },
        { text: this.$t("crudLinhas.detail.allocation.eligibility"), value: "elegivel", sortable: false },
        { text: this.$t("crudLinhas.detail.passengers.group"), value: "id_grupo", sortable: false },
        { text: this.$t("crudLinhas.detail.passengers.boarding"), value: "id_cerca_embarque", sortable: false },
        { text: this.$t("crudLinhas.detail.passengers.alighting"), value: "id_cerca_desembarque", sortable: false },
      ];
    },
    elegiveisProntos() {
      const roundTrip = this.lineHasRoundTrip();
      return (this.validatedRows || []).filter((r) => {
        if (!r || !r.elegivel) return false;
        if (r.id_cerca_embarque == null || r.id_cerca_embarque === "") return false;
        // Ida e volta: exige desembarque (espelha embarque). Ida/volta só: desembarque fica null.
        if (roundTrip && (r.id_cerca_desembarque == null || r.id_cerca_desembarque === "")) {
          return false;
        }
        return true;
      });
    },
    /** Elegíveis que ainda dependem de uma cerca nova (ou de escolha manual do embarque). */
    pendentesNovoPonto() {
      return (this.validatedRows || []).filter(
        (r) =>
          r.elegivel &&
          r.requerNovoPonto &&
          (r.id_cerca_embarque == null || r.id_cerca_embarque === "")
      );
    },
    canAllocate() {
      return this.elegiveisProntos.length > 0 || this.pendentesNovoPonto.length > 0;
    },
  },
  watch: {
    value(open) {
      if (open) this.resetOnOpen();
      else this.clearSchedulerPoll();
    },
    passageiro() {
      if (this.value && this.isEditMode) this.hydrateEdit();
    },
  },
  beforeDestroy() {
    this.clearSchedulerPoll();
  },
  methods: {
    // Exposto ao template: o chip verde e a regra de alocacao olham a validacao.
    isValidado,
    resetOnOpen() {
      this.saving = false;
      this.verifying = false;
      this.verified = false;
      this.selectedIds = [];
      this.validatedRows = [];
      this.clearSchedulerPoll();
      this.$nextTick(() => {
        if (this.$refs.passageirosSelect) this.$refs.passageirosSelect.resetData();
        if (!this.isEditMode) this.syncGrupoSelect(null);
      });
      if (this.isEditMode) this.hydrateEdit();
    },
    onGrupoSelect(val) {
      this.form.id_grupo = val;
    },
    syncGrupoSelect(itemOrId) {
      const ref = this.$refs.grupoSelect;
      if (!ref) return;
      if (itemOrId == null || itemOrId === "") {
        ref.resetData();
        return;
      }
      if (typeof itemOrId === "object") {
        const id = itemOrId.id_grupo;
        if (id == null) {
          ref.resetData();
          return;
        }
        ref.cacheSelectedItems(
          [{
            id_grupo: id,
            nome: itemOrId.nome || itemOrId.nome_grupo || `Grupo ${id}`,
          }],
          "id_grupo"
        );
        ref.setData(id);
        return;
      }
      ref.cacheSelectedItems([{ id_grupo: itemOrId, nome: `Grupo ${itemOrId}` }], "id_grupo");
      ref.setData(itemOrId);
    },
    hydrateEdit() {
      const p = this.passageiro || {};
      const alocadoIda = Number(p.alocado_ida) === 1;
      const alocadoVolta = Number(p.alocado_volta) === 1;
      const validated = isValidado(p);
      // Validado sem flags → default ambos; senão respeita o que já está alocado
      const defaultAmbos = validated && !alocadoIda && !alocadoVolta;
      this.form = {
        id_grupo: p.id_grupo != null ? Number(p.id_grupo) || p.id_grupo : null,
        id_cerca_embarque: this.toFenceId(p.id_cerca_embarque),
        id_cerca_desembarque: this.toFenceId(p.id_cerca_desembarque),
        alocar_ida: defaultAmbos ? true : alocadoIda,
        alocar_volta: defaultAmbos ? true : alocadoVolta,
      };
      this.$nextTick(() => {
        if (this.form.id_grupo != null) {
          this.syncGrupoSelect({
            id_grupo: this.form.id_grupo,
            nome: p.nome_grupo || p.nome || null,
          });
        } else {
          this.syncGrupoSelect(null);
        }
      });
    },
    buildLinhaAllocationPayload(extra) {
      const idLinha = this.linha && this.linha.id_linha;
      const alocarIda = extra && extra.alocar_ida != null ? !!extra.alocar_ida : true;
      const alocarVolta = extra && extra.alocar_volta != null ? !!extra.alocar_volta : true;
      const wasIda = !!(extra && extra.was_ida);
      const wasVolta = !!(extra && extra.was_volta);
      const partial = !!(extra && extra.partial);

      if (!partial) {
        return {
          id_linha_ida: alocarIda ? idLinha : null,
          id_linha_volta: alocarVolta ? idLinha : null,
        };
      }

      // Edição: só grava o sentido ligado à linha atual; não zera o outro se for outra linha
      const row = {};
      if (alocarIda) {
        row.id_linha_ida = idLinha;
      } else if (wasIda) {
        row.id_linha_ida = null;
      } else {
        row.update_linha_ida = false;
      }
      if (alocarVolta) {
        row.id_linha_volta = idLinha;
      } else if (wasVolta) {
        row.id_linha_volta = null;
      } else {
        row.update_linha_volta = false;
      }
      return row;
    },
    close() {
      this.clearSchedulerPoll();
      this.$emit("input", false);
    },
    toFenceId(value) {
      if (value == null || value === "") return null;
      const n = Number(value);
      return Number.isFinite(n) && n > 0 ? n : null;
    },
    fenceValidoCode(bf) {
      if (!bf) return 0;
      if (bf.valido != null) return Number(bf.valido);
      return bf.idFence == null ? 0 : 1;
    },
    /**
     * Linha com cercas de ida e de volta (ou sentido -1) → ida e volta.
     */
    lineHasRoundTrip() {
      const fences = this.cercasAlocaveis || [];
      let hasIda = false;
      let hasVolta = false;
      for (const f of fences) {
        const s = Number(f && f.id_sentido);
        if (!Number.isFinite(s)) continue;
        if (s === -1) return true;
        if (s === 1 || s === 3 || s === 4) hasIda = true;
        if (s === 2 || s === 5 || s === 6) hasVolta = true;
        if (hasIda && hasVolta) return true;
      }
      return false;
    },
    /**
     * Desembarque = cerca de retorno (igual ao embarque), nunca o CD/ponto final.
     * Apenas ida ou apenas volta → null; ida e volta → mesmo id do embarque.
     */
    resolveAlightingFence(idEmbarque) {
      if (!this.lineHasRoundTrip()) return null;
      return this.toFenceId(idEmbarque);
    },
    onFormBoardingChange() {
      this.form.id_cerca_desembarque = this.resolveAlightingFence(this.form.id_cerca_embarque);
    },
    onRowBoardingChange(item) {
      if (!item) return;
      item.id_cerca_desembarque = this.resolveAlightingFence(item.id_cerca_embarque);
    },
    /**
     * Embarque: cerca alocável mais próxima do lat/lng do passageiro.
     * Aceita coords do candidato ou da validação (geocode). Sem coords → null.
     */
    resolveNearestBoardingFence(candidato, positionHint) {
      const fences = this.cercasAlocaveis;
      if (!fences.length) return null;
      let lat = candidato && candidato.lat;
      let lng = candidato && candidato.lng;
      if (lat == null || String(lat).trim() === "" || lng == null || String(lng).trim() === "") {
        const pos = positionHint || {};
        lat = pos.lat != null ? pos.lat : pos.latitude;
        lng = pos.lng != null ? pos.lng : pos.longitude;
      }
      if (lat == null || String(lat).trim() === "" || lng == null || String(lng).trim() === "") {
        return null;
      }
      let best = null;
      let bestDist = Infinity;
      for (const c of fences) {
        if (c == null || c.id_cerca == null) continue;
        const d = haversineMeters(lat, lng, c.lat_centro, c.lng_centro);
        if (Number.isFinite(d) && d < bestDist) {
          bestDist = d;
          best = c;
        }
      }
      return best ? this.toFenceId(best.id_cerca) : null;
    },
    buildPayloadValidacao(candidato) {
      const pcd = candidato.pcd === 1 || candidato.pcd === true ? 1 : 0;
      const base = {
        id_unidade: candidato.id_unidade,
        pessoaIdUnidade: candidato.id_unidade,
        id_pessoa: candidato.id_pessoa,
        id_endereco: candidato.id_endereco,
        endereco: candidato.endereco,
        numero: candidato.numero,
        bairro: candidato.bairro,
        cidade: candidato.cidade,
        estado: candidato.estado,
        cep: candidato.cep,
        sigla_pais: candidato.sigla_pais || candidato.pais,
        checkCapacity: true,
        pcd,
        tryAllUnits: false,
        forceAssignUnit: false,
        nome_completo: candidato.nome_completo,
      };
      const hasLat = candidato.lat != null && String(candidato.lat).trim() !== "";
      const hasLng = candidato.lng != null && String(candidato.lng).trim() !== "";
      if (hasLat && hasLng) {
        base.lat = Number(candidato.lat);
        base.lng = Number(candidato.lng);
      }
      return base;
    },
    clearSchedulerPoll() {
      if (this.schedulerPoll.timer != null) {
        clearInterval(this.schedulerPoll.timer);
        this.schedulerPoll.timer = null;
      }
      this.schedulerPoll.inFlight = false;
    },
    pollSchedulerProcess({ idProcesso, waitMs = 30 * 60 * 1000, pollMs = 5000, onDone, onError }) {
      this.clearSchedulerPoll();
      const deadline = Date.now() + waitMs;
      const failedMsg = this.$t("crudLinhas.detail.allocation.verifyError");

      const pollOnce = () => {
        if (Date.now() > deadline) {
          this.clearSchedulerPoll();
          if (typeof onError === "function") onError(failedMsg);
          return;
        }
        if (this.schedulerPoll.inFlight) return;
        this.schedulerPoll.inFlight = true;

        apiController.request(apiController.endpoints.schedulerGetProcessoschedulerbyid, {
          data: { id_processo: idProcesso },
          config: { timeout: 30000 },
          silent: true,
          onSuccess: (statusRes) => {
            this.schedulerPoll.inFlight = false;
            const pollData = statusRes.data || {};
            if (pollData.erro) {
              this.clearSchedulerPoll();
              if (typeof onError === "function") onError(pollData.mensagem || failedMsg);
              return;
            }
            const processo = pollData.processo;
            const status = processo && processo.status;
            if (status === "PENDING" || status === "EXECUTING") return;
            this.clearSchedulerPoll();
            if (status === "FAILED" || status === "CANCELED") {
              if (typeof onError === "function") {
                onError((processo && processo.mensagem_scheduler) || failedMsg);
              }
              return;
            }
            if (status === "DONE") {
              if (typeof onDone === "function") onDone(pollData.resultado);
              return;
            }
            if (typeof onError === "function") onError(failedMsg);
          },
          onError: (e) => {
            this.schedulerPoll.inFlight = false;
            this.clearSchedulerPoll();
            const msg =
              (e.response &&
                e.response.data &&
                (e.response.data.message || e.response.data.mensagem)) ||
              e.message ||
              failedMsg;
            if (typeof onError === "function") onError(msg);
          },
        });
      };

      this.schedulerPoll.timer = setInterval(pollOnce, pollMs);
      pollOnce();
    },
    rowStatusColor(item) {
      if (!item || !item.elegivel) return "grey";
      return item.requerNovoPonto ? "warning" : "success";
    },
    mapResultToRow(candidato, item) {
      const idLinha = this.linha && this.linha.id_linha != null ? String(this.linha.id_linha) : null;
      const base = {
        id_pessoa: candidato.id_pessoa,
        nome_completo: candidato.nome_completo,
        id_grupo: candidato.id_grupo != null ? candidato.id_grupo : null,
        id_cerca_embarque: null,
        id_cerca_desembarque: null,
        elegivel: false,
        motivo: this.$t("crudLinhas.detail.allocation.notEligible"),
        // Preenchidos quando a validação só atende com criação de ponto (valido = 3).
        requerNovoPonto: false,
        routeLat: null,
        routeLng: null,
        pcd: 0,
        endereco: null,
        lat: null,
        lng: null,
        // Tempo a bordo desta linha. Nunca bloqueia a alocação: só avisa quem estoura o
        // limite configurado, para o operador decidir.
        tempoLinhaExcede: false,
        tempoLinhaTooltip: "",
      };

      if (!item || item.erro) {
        base.motivo =
          (item && item.mensagem) || this.$t("crudLinhas.detail.allocation.verifyFailed");
        return base;
      }

      base.pcd = candidato.pcd === 1 || candidato.pcd === true ? 1 : 0;
      base.endereco = candidato.endereco || null;
      base.lat = candidato.lat != null ? Number(candidato.lat) : null;
      base.lng = candidato.lng != null ? Number(candidato.lng) : null;

      const validation = item.validation || item;
      const bestFences = validation.bestFences || [];
      // validatedFences: todas as cercas no raio (inclui a linha atual mesmo quando
      // bestFences aponta para outra linha como "melhor" global).
      const validatedFences = validation.validatedFences || [];

      // Elegibilidade: hit nesta linha por cerca no raio (lineFenceHit) ou apenas por
      // proximidade da rota (bestRouteHit, valido = 3), que exige criar um ponto novo.
      // No primeiro caso o embarque é a cerca alocável mais próxima do candidato,
      // independente do idFence devolvido pela API.
      let lineFenceHit = null;
      let bestRouteHit = null;

      const considerExisting = (bf) => {
        if (!bf || bf.idLine == null || idLinha == null) return;
        if (String(bf.idLine) !== idLinha) return;
        const v = this.fenceValidoCode(bf);
        // Com checkCapacity, só cerca com vaga (valido=1) torna elegível.
        if (v !== 1) return;
        if (bf.idFence == null) return;
        const dist = Number(bf.distance || Infinity);
        if (!lineFenceHit || dist < Number(lineFenceHit.distance || Infinity)) {
          lineFenceHit = bf;
        }
      };

      const considerRouteHit = (bf) => {
        if (!bf || bf.idLine == null || idLinha == null) return;
        if (String(bf.idLine) !== idLinha) return;
        if (this.fenceValidoCode(bf) !== VALIDO_CRIACAO_PONTO) return;
        const dist = Number(bf.distance || Infinity);
        if (!bestRouteHit || dist < Number(bestRouteHit.distance || Infinity)) {
          bestRouteHit = bf;
        }
      };

      for (const bf of validatedFences) {
        considerExisting(bf);
      }
      for (const bf of bestFences) {
        considerExisting(bf);
        considerRouteHit(bf);
      }

      this.applyRideTimeWarning(base, idLinha, validatedFences, bestFences);

      if (!lineFenceHit && !bestRouteHit) {
        base.motivo = this.$t("crudLinhas.detail.allocation.noFenceOnLine");
        return base;
      }

      if (!(this.cercasAlocaveis || []).length) {
        base.motivo = this.$t("crudLinhas.detail.allocation.noFenceOnLine");
        return base;
      }

      const positionHint = item.position || (validation && validation.latLng) || null;
      if (base.lat == null || base.lng == null) {
        const hint = positionHint || {};
        const hintLat = hint.lat != null ? hint.lat : hint.latitude;
        const hintLng = hint.lng != null ? hint.lng : hint.longitude;
        if (hintLat != null && hintLng != null) {
          base.lat = Number(hintLat);
          base.lng = Number(hintLng);
        }
      }

      // Só atende com ponto novo: deixa o embarque vazio para o fluxo de criação de cerca
      // decidir, guardando o ponto sugerido pela validação (já projetado na rota).
      if (!lineFenceHit && bestRouteHit) {
        const pos = bestRouteHit.position || {};
        const routeLat = Number(pos.latitude != null ? pos.latitude : pos.lat);
        const routeLng = Number(pos.longitude != null ? pos.longitude : pos.lng);
        base.elegivel = true;
        base.requerNovoPonto = true;
        base.motivo = this.$t("crudLinhas.detail.allocation.needsNewStop");
        base.routeLat = Number.isFinite(routeLat) ? routeLat : null;
        base.routeLng = Number.isFinite(routeLng) ? routeLng : null;
        base.id_cerca_desembarque = this.resolveAlightingFence(null);
        return this.forcePcdNewStop(base);
      }

      const idEmbarque = this.resolveNearestBoardingFence(candidato, positionHint);
      if (idEmbarque == null) {
        base.motivo = this.$t("crudLinhas.detail.allocation.noPassengerCoords");
        return base;
      }

      base.elegivel = true;
      base.motivo = "";
      base.id_cerca_embarque = idEmbarque;
      base.id_cerca_desembarque = this.resolveAlightingFence(idEmbarque);
      return this.forcePcdNewStop(base);
    },
    /**
     * Marca a linha quando o candidato ficaria a bordo mais do que o limite configurado.
     * Só sinaliza — a elegibilidade continua decidida por raio, vaga e cerca.
     */
    applyRideTimeWarning(base, idLinha, validatedFences, bestFences) {
      const all = [].concat(validatedFences || [], bestFences || []);
      const hit = all.find(
        (bf) =>
          bf &&
          bf.idLine != null &&
          idLinha != null &&
          String(bf.idLine) === idLinha &&
          bf.exceedsMaxRideTime === true
      );
      if (!hit) return base;
      base.tempoLinhaExcede = true;
      base.tempoLinhaTooltip = this.$t("candidate.rideTime.exceeds", {
        minutes: hit.rideTimeMinutes,
        max: hit.maxRideTimeMinutes,
      });
      return base;
    },
    /**
     * PCD sempre cria ponto no endereço de casa e redesenha a rota (mesmo fluxo da
     * Roteirização / RoteirizacaoAlocacaoDialog.itemsNeedingNewStop).
     */
    forcePcdNewStop(base) {
      if (!base || !base.elegivel || Number(base.pcd) !== 1) return base;
      base.requerNovoPonto = true;
      base.id_cerca_embarque = null;
      base.id_cerca_desembarque = this.resolveAlightingFence(null);
      base.motivo = this.$t("crudLinhas.detail.allocation.needsNewStop");
      if (base.routeLat == null && Number.isFinite(Number(base.lat))) {
        base.routeLat = Number(base.lat);
      }
      if (base.routeLng == null && Number.isFinite(Number(base.lng))) {
        base.routeLng = Number(base.lng);
      }
      return base;
    },
    applyValidationResults(lista, results) {
      const byId = new Map();
      (results || []).forEach((item) => {
        if (item && item.id_pessoa != null) byId.set(String(item.id_pessoa), item);
      });
      this.validatedRows = lista.map((c) => this.mapResultToRow(c, byId.get(String(c.id_pessoa))));
      this.verified = true;
    },
    async verificar() {
      if (!this.linha || this.linha.id_linha == null) return;
      const lista = (this.candidatos || []).filter((c) =>
        this.selectedIds.some((id) => String(id) === String(c.id_pessoa))
      );
      if (!lista.length) {
        EventBus.$emit("show-alert", {
          tipo: "warning",
          mensagem: this.$t("crudLinhas.detail.allocation.selectHint"),
        });
        return;
      }

      this.verifying = true;
      this.verified = false;
      this.validatedRows = [];
      try {
        const payload = {
          candidatos: lista.map((c) => this.buildPayloadValidacao(c)),
          applyUnit: false,
          // Validação pela tela mostra a distância a pé; a API só a calcula quando pedida.
          calcularCaminhada: true,
        };
        const response = await apiController.request(
          apiController.endpoints.validaEnderecocandidato,
          {
            data: payload,
            config: {
              timeout: 180000,
              validateStatus: (s) => s >= 200 && s < 300,
            },
            silent: true,
          }
        );
        const data = response.data || {};
        const status = response.status;

        if (status === 202 && data.id_processo) {
          await new Promise((resolve) => {
            this.pollSchedulerProcess({
              idProcesso: data.id_processo,
              onDone: (resultado) => {
                if (!resultado || resultado.error) {
                  EventBus.$emit("show-alert", {
                    tipo: "error",
                    mensagem:
                      (resultado && (resultado.message || resultado.mensagem)) ||
                      this.$t("crudLinhas.detail.allocation.verifyError"),
                  });
                } else {
                  this.applyValidationResults(lista, resultado.results);
                }
                resolve();
              },
              onError: (msg) => {
                EventBus.$emit("show-alert", { tipo: "error", mensagem: msg });
                resolve();
              },
            });
          });
        } else if (data && (data.mode === "sync" || Array.isArray(data.results))) {
          this.applyValidationResults(lista, data.results);
        } else {
          EventBus.$emit("show-alert", {
            tipo: "error",
            mensagem:
              (data && (data.message || data.mensagem)) ||
              this.$t("crudLinhas.detail.allocation.verifyError"),
          });
        }
      } catch (e) {
        EventBus.$emit("show-error", { error: e });
      } finally {
        this.verifying = false;
      }
    },
    async saveEdit() {
      if (!this.canSaveEdit) return;
      this.saving = true;
      try {
        const response = await apiController.request(apiController.endpoints.alocaPessoasemlinhas, {
          data: {
            passageiros: [
              {
                id_pessoa: this.passageiro.id_pessoa,
                ...this.buildLinhaAllocationPayload({
                  alocar_ida: this.form.alocar_ida,
                  alocar_volta: this.form.alocar_volta,
                  was_ida: Number(this.passageiro.alocado_ida) === 1,
                  was_volta: Number(this.passageiro.alocado_volta) === 1,
                  partial: true,
                }),
                id_grupo: this.form.id_grupo,
                id_cerca_embarque: this.form.id_cerca_embarque,
                id_cerca_desembarque: this.form.id_cerca_desembarque,
              },
            ],
            historico_sessao_id: this.historicoSessaoId || null,
            historico_sessao_origem_id: this.historicoSessaoOrigemId || null,
            historico_origem: "CRUD_LINHAS",
            historico_linha_id: this.linha && this.linha.id_linha,
          },
          silent: true,
        });
        if (response.data && response.data.historicoAviso) {
          EventBus.$emit("show-alert", {
            tipo: "warning",
            mensagem: this.$t("crudLinhas.messages.peopleHistorySaveError"),
          });
        }
        this.$emit("saved");
        this.close();
      } catch (e) {
        EventBus.$emit("show-error", { error: e });
      } finally {
        this.saving = false;
      }
    },
    /**
     * Após verificar, entrega a seleção no formato do RoteirizacaoAlocacaoDialog
     * (criação de cerca valido=3 + PCD /solve).
     */
    alocar() {
      if (!this.canAllocate || !this.linha || this.linha.id_linha == null) return;
      const selection = this.buildAllocationSelection();
      if (!selection.length) return;
      this.$emit("proceed-allocation", {
        selection,
        validatedAddresses: selection,
        pollProcess: (opts) => this.pollSchedulerProcess(opts),
      });
      this.close();
    },
    /**
     * Monta linhas no formato esperado por RoteirizacaoAlocacaoDialog.open / populateAndShow.
     */
    buildAllocationSelection() {
      const idLinha = this.linha && this.linha.id_linha;
      if (idLinha == null) return [];
      const lineMeta = {
        id_linha: idLinha,
        cod_linha:
          (this.linha && (this.linha.cod_linha || this.linha.nome_linha)) ||
          String(idLinha),
      };
      const key = String(idLinha);
      const gruposLinha = (this.grupos || []).map((g) => ({
        ...g,
        id_linha: idLinha,
        id_grupo: g.id_grupo != null ? g.id_grupo : null,
        nome:
          g.nome ||
          g.nome_grupo ||
          g.cod_grupo ||
          (g.id_grupo != null ? `Grupo ${g.id_grupo}` : "-"),
      }));
      const rows = [
        ...this.elegiveisProntos,
        ...this.pendentesNovoPonto.filter(
          (p) =>
            !this.elegiveisProntos.some(
              (e) => String(e.id_pessoa) === String(p.id_pessoa)
            )
        ),
      ];
      return rows.map((r) => {
        const isPcd = Number(r.pcd) === 1 || r.pcd === true;
        const hasBoarding =
          r.id_cerca_embarque != null && r.id_cerca_embarque !== "";
        // PCD sempre cria ponto em casa; sem embarque → criação de cerca (valido=3).
        const needsNew = isPcd || !hasBoarding;
        const valido =
          isPcd || r.requerNovoPonto || !hasBoarding ? VALIDO_CRIACAO_PONTO : 1;
        const candidato =
          (this.candidatos || []).find(
            (c) => String(c.id_pessoa) === String(r.id_pessoa)
          ) || {};
        const melhor =
          !needsNew && hasBoarding
            ? { [key]: { id_cerca: r.id_cerca_embarque, distance: 0 } }
            : {};
        const position =
          r.routeLat != null && r.routeLng != null
            ? { [key]: { latitude: Number(r.routeLat), longitude: Number(r.routeLng) } }
            : {};
        return {
          id_pessoa: r.id_pessoa,
          matricula: candidato.matricula || r.matricula || null,
          nome: r.nome_completo,
          nome_completo: r.nome_completo,
          id_situacao_validacao:
            candidato.id_situacao_validacao != null
              ? Number(candidato.id_situacao_validacao)
              : SITUACAO_VALIDACAO.VALIDADO,
          nome_situacao_validacao: candidato.nome_situacao_validacao || null,
          cod_unidade: candidato.cod_unidade || candidato.descricao_unidade || "-",
          id_unidade: candidato.id_unidade != null ? candidato.id_unidade : r.id_unidade,
          id_grupo: r.id_grupo != null ? r.id_grupo : null,
          id_grupo_alocado: r.id_grupo != null ? r.id_grupo : null,
          id_linha_alocada: idLinha,
          id_cerca_embarque: needsNew ? null : r.id_cerca_embarque,
          id_cerca_desembarque: needsNew ? null : r.id_cerca_desembarque,
          grupos_linha: gruposLinha,
          linhas_validadas: [lineMeta],
          melhor_cerca_por_linha: melhor,
          valido_por_linha: { [key]: valido },
          position_por_linha: position,
          pcd: isPcd ? 1 : 0,
          endereco: r.endereco || candidato.endereco || "",
          lat: r.lat != null ? Number(r.lat) : null,
          lng: r.lng != null ? Number(r.lng) : null,
        };
      });
    },
  },
};
</script>
